import { Trans, useLingui } from '@lingui/react/macro'
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from 'designSystem/components/Dialog'
import {
  boxSpan,
  cropCanvasSize,
  cropFallbackBackground,
} from 'features/knowledgeDocumentViewer/components/revisionCompare/revisionCropGeometry'
import { useImageStatus } from 'features/knowledgeDocumentViewer/components/revisionCompare/useImageStatus'
import { useImageNaturalSize } from 'features/knowledgeDocumentViewer/components/viewer/image/hooks/useImageNaturalSize'
import type {
  RevisionDiffDetailBox,
  RevisionObjectStatus,
} from 'features/knowledgeDocumentViewer/hooks/useRevisionDiffDetails'
import { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'

const INLINE_MAX_INTRINSIC_PX = 800
const EXPANDED_MAX_INTRINSIC_PX = 2400

type CropVariant = 'inline' | 'expanded'

const Root = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
`

const CropFigure = styled.figure`
  display: flex;
  min-width: 0;
  flex: 1 1 12rem;
  flex-direction: column;
  gap: 0.25rem;
  margin: 0;
`

const CropCaption = styled.figcaption`
  color: var(--muted-foreground);
  font-size: 0.6875rem;
`

const ExpandButton = styled.button`
  display: block;
  width: 100%;
  margin: 0;
  padding: 0;
  border: none;
  background: none;
  cursor: zoom-in;
  text-align: left;
`

const CropCanvasElement = styled.canvas<{ $variant: CropVariant }>`
  max-width: 100%;
  max-height: ${(props) => (props.$variant === 'expanded' ? '80vh' : '18rem')};
  width: auto;
  height: auto;
  border: 0.0625rem solid var(--border);
  border-radius: 0.25rem;
`

const CropUnavailable = styled.div<{ $aspect: number }>`
  display: flex;
  width: 100%;
  min-height: 4rem;
  align-items: center;
  justify-content: center;
  aspect-ratio: ${(props) => props.$aspect};
  max-height: 18rem;
  border: 0.0625rem dashed var(--border);
  border-radius: 0.25rem;
  color: var(--muted-foreground);
  font-size: 0.75rem;
`

const CropFallback = styled.div<{
  $url: string
  $aspect: number
  $sizeX: number
  $sizeY: number
  $posX: number
  $posY: number
  $variant: CropVariant
}>`
  width: 100%;
  aspect-ratio: ${(props) => props.$aspect};
  max-height: ${(props) => (props.$variant === 'expanded' ? '80vh' : '18rem')};
  border: 0.0625rem solid var(--border);
  border-radius: 0.25rem;
  background-image: url(${(props) => props.$url});
  background-repeat: no-repeat;
  background-size: ${(props) => props.$sizeX}% ${(props) => props.$sizeY}%;
  background-position: ${(props) => props.$posX}% ${(props) => props.$posY}%;
`

type CropViewProps = {
  imageUrl: string | undefined
  box: RevisionDiffDetailBox
  caption: string
  variant: CropVariant
}

function CropImage({ imageUrl, box, caption, variant }: CropViewProps) {
  const natural = useImageNaturalSize(imageUrl ?? null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [canDraw, setCanDraw] = useState(true)
  const status = useImageStatus(imageUrl ?? null)

  const widthFraction = boxSpan(box.x0, box.x1)
  const heightFraction = boxSpan(box.y0, box.y1)
  const aspect = natural
    ? (widthFraction * natural.w) / (heightFraction * natural.h)
    : 1
  const cropW = natural ? widthFraction * natural.w : 1
  const cropH = natural ? heightFraction * natural.h : 1
  const maxIntrinsic =
    variant === 'expanded' ? EXPANDED_MAX_INTRINSIC_PX : INLINE_MAX_INTRINSIC_PX
  const { canvasWidth, canvasHeight } = cropCanvasSize({
    cropW,
    cropH,
    maxIntrinsic,
  })

  useEffect(() => {
    setCanDraw(true)
  }, [imageUrl])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!imageUrl || !natural || !canvas) return
    const context = canvas.getContext('2d')
    if (!context) {
      setCanDraw(false)
      return
    }
    let cancelled = false
    const image = new Image()
    image.onerror = () => {
      if (!cancelled) setCanDraw(false)
    }
    image.onload = () => {
      if (cancelled) return
      context.clearRect(0, 0, canvas.width, canvas.height)
      context.drawImage(
        image,
        box.x0 * natural.w,
        box.y0 * natural.h,
        widthFraction * natural.w,
        heightFraction * natural.h,
        0,
        0,
        canvas.width,
        canvas.height
      )
    }
    image.src = imageUrl
    return () => {
      cancelled = true
    }
  }, [imageUrl, natural, box, widthFraction, heightFraction])

  if (status === 'error') {
    return (
      <CropUnavailable role='img' aria-label={caption} $aspect={aspect}>
        <Trans>Crop unavailable</Trans>
      </CropUnavailable>
    )
  }

  const fallback = cropFallbackBackground(box)

  return imageUrl && !canDraw ? (
    <CropFallback
      $variant={variant}
      role='img'
      aria-label={caption}
      $url={imageUrl}
      $aspect={aspect}
      $sizeX={fallback.sizeX}
      $sizeY={fallback.sizeY}
      $posX={fallback.posX}
      $posY={fallback.posY}
    />
  ) : (
    <CropCanvasElement
      $variant={variant}
      ref={canvasRef}
      role='img'
      aria-label={caption}
      width={canvasWidth}
      height={canvasHeight}
    />
  )
}

function CropView({ imageUrl, box, caption }: Omit<CropViewProps, 'variant'>) {
  const { t } = useLingui()
  const [expanded, setExpanded] = useState(false)

  return (
    <CropFigure>
      <ExpandButton
        type='button'
        aria-label={t`Expand ${caption}`}
        onClick={() => setExpanded(true)}>
        <CropImage
          imageUrl={imageUrl}
          box={box}
          caption={caption}
          variant='inline'
        />
      </ExpandButton>
      <CropCaption>{caption}</CropCaption>
      <Dialog open={expanded} onOpenChange={setExpanded}>
        <DialogContent aria-describedby={undefined}>
          <DialogTitle srOnly>{caption}</DialogTitle>
          <CropImage
            imageUrl={imageUrl}
            box={box}
            caption={caption}
            variant='expanded'
          />
        </DialogContent>
      </Dialog>
    </CropFigure>
  )
}

type RevisionChangeCropPairProps = {
  status: RevisionObjectStatus
  fromImageUrl: string | undefined
  toImageUrl: string | undefined
  fromBox: RevisionDiffDetailBox | null
  toBox: RevisionDiffDetailBox | null
}

/**
 * Before/after crops of a change region, cut client-side from the page
 * renders already loaded for the panes. Added changes only exist after, and
 * removed changes only exist before, so each shows a single crop.
 */
export function RevisionChangeCropPair({
  status,
  fromImageUrl,
  toImageUrl,
  fromBox,
  toBox,
}: RevisionChangeCropPairProps) {
  const { t } = useLingui()
  const showBefore = status !== 'added' ? fromBox : null
  const showAfter = status !== 'removed' ? toBox : null
  if (!showBefore && !showAfter) return null

  return (
    <Root>
      {showBefore ? (
        <CropView
          imageUrl={fromImageUrl}
          box={showBefore}
          caption={t`Before`}
        />
      ) : null}
      {showAfter ? (
        <CropView imageUrl={toImageUrl} box={showAfter} caption={t`After`} />
      ) : null}
    </Root>
  )
}
